import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format an ISO 8601 date string as a relative time string.
 * e.g. "just now", "5 minutes ago", "2 hours ago", "3 days ago"
 * No external libraries — pure arithmetic.
 */
export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString)
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return 'gerade eben'
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60)
    return `vor ${mins} ${mins === 1 ? 'Minute' : 'Minuten'}`
  }
  if (diffSec < 86400) {
    const hours = Math.floor(diffSec / 3600)
    return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`
  }
  if (diffSec < 604800) {
    const days = Math.floor(diffSec / 86400)
    return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`
  }
  if (diffSec < 2592000) {
    const weeks = Math.floor(diffSec / 604800)
    return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`
  }
  const months = Math.floor(diffSec / 2592000)
  return `vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`
}

/**
 * Format an ISO 8601 date string as an "HH:mm" time string in the
 * Europe/Berlin timezone. Used for compact time labels in list rows
 * (appointments, calendar chips).
 */
export function formatTimeBerlin(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  })
}

/**
 * Format an ISO 8601 date string as an absolute datetime string.
 * e.g. "Mon, 1 Mar 2026, 14:32"
 * Locale-aware via Intl.DateTimeFormat.
 */
export function formatAbsoluteDate(isoString: string): string {
  const date = new Date(isoString)
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/**
 * Format a byte count as a human-readable file size string.
 * e.g. "512 B", "1.2 KB", "3.45 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

/**
 * Safely redirect to an OAuth authorization URL returned from the API.
 *
 * Only allows HTTPS URLs to prevent open-redirect attacks where a compromised
 * or misconfigured backend returns an attacker-controlled URL.
 * HTTP is blocked (even for localhost) to enforce transport security.
 *
 * @param url - The authorization URL from the backend API response.
 * @throws Error if the URL is not a valid HTTPS URL.
 */
export function safeOAuthRedirect(url: string): void {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') {
      throw new Error(`Blocked OAuth redirect to non-HTTPS URL: ${parsed.protocol}`)
    }
    window.location.href = url
  } catch (err) {
    // Re-throw validation errors; catch only URL parse errors and convert them
    if (err instanceof TypeError) {
      throw new Error(`Blocked OAuth redirect to invalid URL: ${url}`)
    }
    throw err
  }
}
